import { makeStyles, tokens, Toaster } from '@fluentui/react-components';
import { useCallback } from 'react';
import { SearchHeader } from './SearchHeader';
import { useUiStore, useSearchQuery } from '@/stores/uiStore';
import { useTreeStore } from '@/stores/treeStore';
import { useMailDropStore } from '@/stores/mailDropStore';
import type { DropTarget } from '@/services/office/dropHitTest';
import { TreeView } from '@/features/tree/TreeView';
import { FolderDetail } from '@/features/folder-detail/FolderDetail';
import { DropZone } from '@/features/file-mail/DropZone';
import { useOfficeDragDrop } from '@/features/file-mail/useOfficeDragDrop';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { QuickFile } from '@/features/quick-file/QuickFile';
import { SpaceCreate } from '@/features/space-create/SpaceCreate';
import { SearchResults } from '@/features/search/SearchResults';
import { SettingsView } from '@/features/settings/SettingsView';
import { TabBar } from '@/features/tabs/TabBar';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
  },
  // Wraps only the scrollable list region (not the tabs / search header), so
  // the drop overlay highlights the LIST rather than the whole pane. It does
  // not scroll itself — the inner .content does — so the overlay stays put over
  // the visible list.
  contentWrap: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
  },
  // "Drop here" affordance over the list. SEMI-TRANSPARENT fill so the folders
  // underneath stay visible (the user needs to see them to aim at one — moving
  // onto a folder hides this overlay and lights that folder up instead).
  // pointer-events:none so it never intercepts the Office hit-test / HTML5 drop.
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: `color-mix(in srgb, ${tokens.colorBrandBackground2} 40%, transparent)`,
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
});

export function TaskPaneShell() {
  const styles = useStyles();
  const view = useUiStore((s) => s.view);
  // Terme de recherche de l'onglet ACTIF : un onglet ouvert depuis un résultat de
  // recherche doit afficher sa page, pas la liste de résultats de l'onglet d'où
  // il vient.
  const searchQuery = useSearchQuery();
  const { toasterId } = useFileMailToasts();

  // Fallback target for a drop that misses every folder/project. On the
  // folder-detail page it's the current folder (or the container root when
  // viewing a project root). On the HOME tree it's null: a drop outside any
  // project files nowhere — no fallback to a selected folder. A drop that lands
  // on a folder/mail/project files into that target instead.
  const getFallback = useCallback((): DropTarget | null => {
    const s = useTreeStore.getState();
    if (s.mode !== 'detail') return null;
    if (s.detailFolderId) return { kind: 'folder', folderId: s.detailFolderId };
    return s.activeContainerId ? { kind: 'root', containerId: s.activeContainerId } : null;
  }, []);

  // New Outlook / web: the Office DragAndDropEvent carries taskPaneX/Y, so the
  // hook hit-tests the target under the cursor (folder / project root / none)
  // and files there. Highlight + auto-clear are owned by the hook (Classic
  // desktop / Mac drive the same store via HTML5 handlers on FolderRow /
  // ContainerSection / DropZone).
  useOfficeDragDrop(getFallback);

  const detailFolderId = useTreeStore((s) => s.detailFolderId);
  const activeContainerId = useTreeStore((s) => s.activeContainerId);
  const mode = useTreeStore((s) => s.mode);
  const fallbackTarget: DropTarget | null =
    mode !== 'detail'
      ? null
      : detailFolderId
        ? { kind: 'folder', folderId: detailFolderId }
        : activeContainerId
          ? { kind: 'root', containerId: activeContainerId }
          : null;
  // The list-scoped "drop here" overlay only makes sense on the detail page,
  // where blank space still has a valid fallback (the current folder/root). On
  // the home tree there is no whole-view target, so no overlay.
  const dragging = useMailDropStore((s) => s.dragging);
  const dropTarget = useMailDropStore((s) => s.target);
  const showDropOverlay = dragging && dropTarget === null && mode === 'detail';
  // Active search hijacks the content area entirely - the tree / quick-file /
  // space-create views resume as soon as the search box is cleared.
  const isSearching = searchQuery.trim().length > 0;

  return (
    <DropZone fallbackTarget={fallbackTarget}>
      <div className={styles.root}>
        <TabBar />
        <SearchHeader />
        <Toaster toasterId={toasterId} />
        <div className={styles.contentWrap}>
          <div className={styles.content}>
            {isSearching ? (
              <SearchResults />
            ) : (
              <>
                {view === 'tree' && (mode === 'detail' ? <FolderDetail /> : <TreeView />)}
                {view === 'quickFile' && <QuickFile />}
                {view === 'spaceCreate' && <SpaceCreate />}
                {view === 'settings' && <SettingsView />}
              </>
            )}
          </div>
          {showDropOverlay && (
            <div className={styles.dropOverlay} aria-hidden>
              Déposez les mails ici pour les ranger
            </div>
          )}
        </div>
      </div>
    </DropZone>
  );
}
